import React, { useMemo } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useIntl } from 'react-intl';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { formatLastSyncTimeLocalized } from './catalogSyncUtils';
import { CheckCircle } from '@material-ui/icons';
import styles from './CatalogHistoryPage.module.scss';
import { useRuntimeIndexing } from './useRuntimeIndexing';
import { isIndexingActive } from './indexingRuntimeUtils';
import { catalogSourcePhaseLabel, catalogSourceProgress } from './catalogSourcePhase';

function statusClass(status) {
  if (status === 'completed') return styles.statusCompleted;
  if (status === 'running' || status === 'queued') return styles.statusRunning;
  if (status === 'failed') return styles.statusFailed;
  return styles.statusNeutral;
}

function localizedJobStatus(status, localizeMessage) {
  const knownStatuses = ['completed', 'running', 'queued', 'failed', 'idle'];
  const safeStatus = knownStatuses.includes(status) ? status : 'unknown';
  return localizeMessage(`search.page.catalogHistory.job.${safeStatus}`);
}

function formatDate(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

const CatalogHistoryPage = () => {
  const intl = useIntl();
  const localizeMessage = useLocalizedMessage();
  const {
    snapshot,
    isLoading,
    isRefreshing,
    isStarting,
    error,
    start,
  } = useRuntimeIndexing();
  const { current, history, lastSuccessAt } = snapshot;
  const indexingStatus = current?.status || (lastSuccessAt ? 'ready' : 'idle');
  const indexingProgress = typeof current?.progress === 'number' ? current.progress : 0;
  const hasIndexingProgress = typeof current?.progress === 'number';
  const indexedCount = current?.indexedCount;
  const totalCount = current?.totalCount;
  const sourceLabel = catalogSourcePhaseLabel(current, localizeMessage);
  const sourceProgress = catalogSourceProgress(current);
  const displayProgress = sourceLabel
    ? sourceProgress
    : hasIndexingProgress ? indexingProgress : null;

  const statusLabel = useMemo(() => {
    if (sourceLabel) return sourceLabel;
    if (indexingStatus === 'running') {
      if (typeof indexedCount === 'number' && totalCount > 0) {
        return localizeMessage('search.page.catalogHistory.status.indexed', {
          indexed: indexedCount.toLocaleString(intl.locale),
          total: totalCount.toLocaleString(intl.locale),
        });
      }
      return hasIndexingProgress
        ? localizeMessage('search.page.catalogHistory.status.progress', { progress: indexingProgress })
        : localizeMessage('search.page.catalogHistory.status.running');
    }

    if (indexingStatus === 'ready') {
      return lastSuccessAt
        ? localizeMessage('search.page.catalogHistory.status.lastUpdate', {
          time: formatLastSyncTimeLocalized(lastSuccessAt, localizeMessage),
        })
        : localizeMessage('search.page.catalogHistory.status.ready');
    }
    if (indexingStatus === 'error') {
      return localizeMessage('search.page.catalogHistory.status.failed');
    }
    return localizeMessage('search.page.catalogHistory.status.idle');
  }, [
    indexingStatus,
    indexingProgress,
    hasIndexingProgress,
    indexedCount,
    intl.locale,
    lastSuccessAt,
    localizeMessage,
    totalCount,
    sourceLabel,
  ]);

  const sidebar = (
    <div className={styles.sidebarCard}>
      <div>
        <p className={styles.sidebarLabel}>
          {localizeMessage('search.page.catalogHistory.sidebar.title')}
        </p>
        <p className={styles.sidebarHint}>
          {localizeMessage('search.page.catalogHistory.sidebar.description')}
        </p>
      </div>
      <div className={styles.sidebarAction}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          className={styles.updateButton}
          onClick={start}
          disabled={isStarting || isIndexingActive(current)}
          data-testid="start-full-reindex-btn"
        >
          {isStarting
            ? localizeMessage('search.page.catalogHistory.sidebar.starting')
            : localizeMessage('search.page.catalogHistory.sidebar.update')}
        </Button>
      </div>
      <div className={styles.sidebarCheck}>
        <CheckCircle className={styles.sidebarCheckIcon} style={{ fontSize: 16 }} />
        <span data-testid="catalog-progress-label">{statusLabel}</span>
      </div>
      {isIndexingActive(current) && (
        <LinearProgress
          variant={displayProgress !== null ? 'determinate' : 'indeterminate'}
          value={displayProgress ?? undefined}
          data-testid="indexing-progress-bar"
        />
      )}
    </div>
  );

  return (
    <SearchFeaturePageShell
      pageId="catalogHistory"
      description={localizeMessage('search.page.catalogHistory.description')}
      isLoading={isLoading && history.length === 0}
      sidebar={sidebar}
    >
      <section className={styles.historyCard}>
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle}>
              {localizeMessage('search.page.catalogHistory.history.title')}
            </h2>
            <p className={styles.cardDescription}>
              {localizeMessage('search.page.catalogHistory.history.description')}
            </p>
          </div>
          {isRefreshing && history.length > 0 && (
            <span className={styles.refreshing}>
              <CircularProgress size={16} />
              {localizeMessage('search.page.catalogHistory.history.refreshing')}
            </span>
          )}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table} data-testid="catalog-history-table">
              <thead className="th-border-b">
                <tr>
                  <th>{localizeMessage('search.page.catalogHistory.table.status')}</th>
                  <th>{localizeMessage('search.page.catalogHistory.table.progress')}</th>
                  <th>{localizeMessage('search.page.catalogHistory.table.indexed')}</th>
                  <th>{localizeMessage('search.page.catalogHistory.table.started')}</th>
                  <th>{localizeMessage('search.page.catalogHistory.table.finished')}</th>
                  <th>{localizeMessage('search.page.catalogHistory.table.result')}</th>
                </tr>
              </thead>
              <tbody>
                {error && (
                  <tr>
                    <td colSpan={6} className={styles.errorState}>{error}</td>
                  </tr>
                )}

                {!error && history.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      {localizeMessage('search.page.catalogHistory.table.empty')}
                    </td>
                  </tr>
                )}

                {!error && history.map((job, index) => (
                  <tr key={job.jobId || `${job.createdAt}-${index}`}>
                    <td>
                      <Chip
                        size="small"
                        label={localizedJobStatus(job.status, localizeMessage)}
                        className={`${styles.statusChip} ${statusClass(job.status)}`}
                      />
                    </td>
                    <td>{catalogSourcePhaseLabel(job, localizeMessage) || (typeof job.progress === 'number' ? `${job.progress}%` : '—')}</td>
                    <td>
                      {typeof job.indexedCount === 'number' && typeof job.totalCount === 'number'
                        ? `${job.indexedCount} / ${job.totalCount}`
                        : '—'}
                    </td>
                    <td>{formatDate(job.startedAt || job.createdAt, intl.locale)}</td>
                    <td>{formatDate(job.finishedAt, intl.locale)}</td>
                    <td className={styles.messageCell} title={job.error || undefined}>
                      {job.error || (job.status === 'completed'
                        ? localizeMessage('search.page.catalogHistory.table.success')
                        : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
          </table>
        </div>

      </section>
    </SearchFeaturePageShell>
  );
};

export default CatalogHistoryPage;
